import { useMemo } from 'react';
import ReactFlow, { MiniMap, Node as ReactFlowNode } from 'react-flow-renderer';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import API from 'api';
import Card from 'components/Card';
import { ErrorContent } from 'components/FetchingErrorBoundary';
import Loading from 'components/Icons/Loading';
import { COLOR } from 'styles/styledComponents/color.model';

import { CONNECTION_TREE_QUERY } from '../../api/connectionTree/connectionTree.model';
import { getEdges, getNodes } from './ConnectionTreePage.helpers';
import * as Styled from './ConnectionTreePage.styles';

const ConnectionTreePage = () => {
  const { id } = useParams<{ id: string }>();
  const {
    data: certificateTree = { nodes: [], edges: [] },
    isLoading,
    refetch,
  } = useQuery(
    [CONNECTION_TREE_QUERY.CERTIFICATE_TREE, id],
    () => API.documents.getDocumentsIDTree(id ?? ''),
    {
      enabled: Boolean(id),
      useErrorBoundary: true,
    }
  );
  const certificateTreeStringified = JSON.stringify(certificateTree);

  const nodes = useMemo(
    () => getNodes(certificateTree),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [certificateTreeStringified]
  );
  const edges = useMemo(
    () => getEdges(certificateTree, nodes),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [certificateTreeStringified, nodes]
  );

  const getNodeColor = (node: ReactFlowNode) =>
    node.type === 'default' ? COLOR.CHEMISTRY : COLOR.OUTER_SPACE;

  if (isLoading) return <Loading />;
  if (!certificateTree) return <ErrorContent onRetry={refetch} />;
  return (
    <Card backButtonLink="/home/certificates">
      <Styled.PageContainer>
        <Styled.TreeContainer>
          <ReactFlow
            defaultNodes={nodes}
            defaultEdges={edges}
            style={{ width: '100%', height: '100%' }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
          >
            <MiniMap nodeStrokeWidth={3} nodeStrokeColor={getNodeColor} />
          </ReactFlow>
        </Styled.TreeContainer>
      </Styled.PageContainer>
    </Card>
  );
};

export default ConnectionTreePage;
